import { apiClient } from './api'
import type { Coupon, CouponUsage, CouponValidationResult, ValidateCouponRequest, ApplyCouponRequest, CreateCouponInput, UpdateCouponInput } from '@/types/coupon'

interface ApiResponse<T> {
  success: boolean
  data: T
  error?: {
    code: string
    message: string
  }
}

async function api<T>(promise: Promise<unknown>): Promise<ApiResponse<T>> {
  return promise as unknown as ApiResponse<T>
}

function transformCoupon(data: Record<string, unknown>): Coupon {
  return {
    id: data.id as string,
    code: data.code as string,
    name: data.name as string,
    description: data.description as string | undefined,
    discountType: data.discount_type as 'percentage' | 'fixed',
    discountValue: data.discount_value as number,
    affiliateId: data.affiliate_id as string | undefined,
    maxUses: data.max_uses as number | undefined,
    maxUsesPerUser: data.max_uses_per_user as number,
    currentUses: data.current_uses as number,
    minimumPurchase: data.minimum_purchase as number,
    validFrom: data.valid_from as string | undefined,
    validUntil: data.valid_until as string | undefined,
    isActive: data.is_active as boolean,
    createdAt: data.created_at as string,
    updatedAt: data.updated_at as string,
  }
}

function transformValidationResult(data: Record<string, unknown>): CouponValidationResult {
  return {
    valid: data.valid as boolean,
    discountAmount: data.discount_amount as number,
    finalPrice: data.final_price as number,
    coupon: data.coupon ? transformCoupon(data.coupon as Record<string, unknown>) : undefined,
    errorCode: data.error_code as string | undefined,
    errorMessage: data.error_message as string | undefined,
  }
}

export const couponService = {
  validateCoupon: async (request: ValidateCouponRequest): Promise<CouponValidationResult> => {
    const response = await api<{ data: Record<string, unknown> }>(
      apiClient.post('/coupons/validate', {
        code: request.code,
        original_price: request.originalPrice,
      })
    )
    if (!response.success) {
      throw new Error('Invalid response from server')
    }
    return transformValidationResult(response.data.data)
  },

  applyCoupon: async (request: ApplyCouponRequest): Promise<void> => {
    await apiClient.post('/coupons/apply', {
      coupon_id: request.couponId,
      enrollment_id: request.enrollmentId,
      discount_amount: request.discountAmount,
    })
  },

  getCouponByCode: async (code: string): Promise<Coupon> => {
    const response = await api<{ data: Record<string, unknown> }>(
      apiClient.get(`/coupons/code/${code}`)
    )
    if (!response.success) {
      throw new Error('Coupon not found')
    }
    return transformCoupon(response.data.data)
  },

  listCoupons: async (params?: { isActive?: boolean; search?: string; limit?: number; offset?: number }): Promise<Coupon[]> => {
    const queryParams = new URLSearchParams()
    if (params?.isActive !== undefined) {
      queryParams.append('is_active', String(params.isActive))
    }
    if (params?.search) {
      queryParams.append('search', params.search)
    }
    if (params?.limit) {
      queryParams.append('limit', String(params.limit))
    }
    if (params?.offset) {
      queryParams.append('offset', String(params.offset))
    }

    const query = queryParams.toString()
    const url = query ? `/admin/coupons?${query}` : '/admin/coupons'
    const response = await api<Record<string, unknown> | Record<string, unknown>[]>(
      apiClient.get(url)
    )
    if (!response.success) {
      return []
    }
    const raw = response.data
    const items = Array.isArray(raw) ? raw : (raw?.data as Record<string, unknown>[] | undefined)
    if (!items) return []
    return items.map(transformCoupon)
  },

  getCoupon: async (id: string): Promise<Coupon> => {
    const response = await api<Coupon>(
      apiClient.get(`/admin/coupons/${id}`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Kupon tidak ditemukan')
    }
    return transformCoupon(response.data as unknown as Record<string, unknown>)
  },

  createCoupon: async (input: CreateCouponInput): Promise<Coupon> => {
    const response = await api<Coupon>(
      apiClient.post('/admin/coupons', input)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal membuat kupon')
    }
    return transformCoupon(response.data as unknown as Record<string, unknown>)
  },

  updateCoupon: async (id: string, input: UpdateCouponInput): Promise<Coupon> => {
    const response = await api<Coupon>(
      apiClient.put(`/admin/coupons/${id}`, input)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal mengupdate kupon')
    }
    return transformCoupon(response.data as unknown as Record<string, unknown>)
  },

  deleteCoupon: async (id: string): Promise<void> => {
    const response = await api<null>(
      apiClient.delete(`/admin/coupons/${id}`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal menghapus kupon')
    }
  },

  activateCoupon: async (id: string): Promise<void> => {
    const response = await api<null>(
      apiClient.post(`/admin/coupons/${id}/activate`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal mengaktifkan kupon')
    }
  },

  deactivateCoupon: async (id: string): Promise<void> => {
    const response = await api<null>(
      apiClient.post(`/admin/coupons/${id}/deactivate`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal menonaktifkan kupon')
    }
  },

  getCouponUsages: async (id: string): Promise<CouponUsage[]> => {
    const response = await api<CouponUsage[]>(
      apiClient.get(`/admin/coupons/${id}/usages`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal mengambil data penggunaan')
    }
    return response.data || []
  },
}
